/**
 * Spec Phase 4 Slice 4D — PaperEditor.
 * BUG-045 — continuous scroll: every section renders inline as
 * its own paper block; the left nav highlight tracks the
 * IntersectionObserver, click-to-nav scrolls smoothly via a
 * window event ("spec.scroll-to-section"). Toolbar + breadcrumb
 * + footer continue to read activeSectionId so they show
 * controls for whichever section is currently in view.
 *
 * Excluded sections (BUG-033) drop out of the scroll.
 */
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSpecStore } from '@/store/specStore';
import SectionBreadcrumb from './SectionBreadcrumb';
import ActionBar from './ActionBar';
import PaperSurface from './PaperSurface';
import ClaimAnchorWarningBanner from './ClaimAnchorWarningBanner';
import UnregisteredNumeralBanner from './UnregisteredNumeralBanner';
import RemovedFigureBanner from './RemovedFigureBanner';
import GenerationFindingsBanner from './GenerationFindingsBanner';
import SectionDiffPanel from './SectionDiffPanel';
import PromptBar from './PromptBar';
import FooterStatusStrip from './FooterStatusStrip';

const SCROLL_REQUEST_EVENT = 'spec.scroll-to-section';

export default function PaperEditor(): JSX.Element {
  const sections = useSpecStore((s) => s.sections);
  const activeSectionId = useSpecStore((s) => s.activeSectionId);
  const setActiveSection = useSpecStore((s) => s.setActiveSection);
  const loadActiveSectionParagraphs = useSpecStore(
    (s) => s.loadActiveSectionParagraphs,
  );

  const visibleSections = useMemo(
    () => sections.filter((s) => !s.is_excluded),
    [sections],
  );
  const activeSection = useMemo(
    () => (activeSectionId
      ? visibleSections.find((s) => s.id === activeSectionId) ?? null
      : null),
    [visibleSections, activeSectionId],
  );

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  // Suppress the intersection observer briefly after a programmatic
  // scroll so the smooth-scroll's transit through other sections
  // doesn't fire setActiveSection mid-flight.
  const programmaticScrollUntilRef = useRef<number>(0);

  // ── Lazy-prefetch every non-auto section's paragraphs so the
  // continuous scroll has content to render. Auto-generated
  // sections (claims_mirror, reference_signs_list) self-fetch via
  // ``AutoGeneratedSection``; we leave them alone.
  useEffect(() => {
    if (visibleSections.length === 0) return;
    visibleSections.forEach((s) => {
      if (s.is_auto_generated) return;
      void loadActiveSectionParagraphs(s.id);
    });
  }, [visibleSections, loadActiveSectionParagraphs]);

  // ── IntersectionObserver — track which section block dominates
  // the viewport. The rootMargin shifts the trigger to the top
  // 30% of the scroll container so a section becomes "active"
  // when its heading scrolls into the upper third (matches the
  // cue's spec). Threshold 0 keeps callbacks cheap; we
  // disambiguate among intersecting entries by sorting by
  // ``boundingClientRect.top`` and taking the first one whose
  // top has crossed below the trigger line.
  useEffect(() => {
    const root = scrollContainerRef.current;
    if (!root) return;
    if (visibleSections.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (Date.now() < programmaticScrollUntilRef.current) return;
        const intersecting = entries.filter((e) => e.isIntersecting);
        if (intersecting.length === 0) return;
        intersecting.sort(
          (a, b) => a.boundingClientRect.top - b.boundingClientRect.top,
        );
        const winner = intersecting.find(
          (e) => e.boundingClientRect.top >= 0,
        ) ?? intersecting[intersecting.length - 1];
        const id = winner.target.getAttribute('data-section-id');
        if (id && id !== activeSectionId) setActiveSection(id);
      },
      {
        root,
        rootMargin: '0px 0px -70% 0px',
        threshold: 0,
      },
    );
    visibleSections.forEach((s) => {
      const el = sectionRefs.current[s.id];
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [visibleSections, activeSectionId, setActiveSection]);

  // ── Listen for left-nav clicks. SectionNav dispatches
  // ``spec.scroll-to-section`` when the drafter clicks a row;
  // PaperEditor scrolls smoothly to that block. Observer-
  // initiated activeSectionId changes don't fire the event so
  // there's no scroll loop.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ id?: string }>)?.detail;
      const id = detail?.id || null;
      if (!id) return;
      const el = sectionRefs.current[id];
      if (!el) return;
      programmaticScrollUntilRef.current = Date.now() + 800;
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    window.addEventListener(SCROLL_REQUEST_EVENT, handler);
    return () => window.removeEventListener(SCROLL_REQUEST_EVENT, handler);
  }, []);

  const setSectionRef = useCallback(
    (id: string) => (el: HTMLDivElement | null) => {
      sectionRefs.current[id] = el;
    },
    [],
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="spec-paper-editor">
      <div
        ref={scrollContainerRef}
        className="s6-paper-backdrop flex-1 overflow-y-auto"
        data-testid="spec-paper-scroll-container"
      >
        {/* Sticky header for the scroll context. SectionBreadcrumb
            + ActionBar pin to the top of the scroll viewport so
            the drafter keeps full access to controls + section
            context while reviewing a long section. Backed by a
            white panel so paper-area scrolling content doesn't
            bleed through. */}
        <div
          className="sticky top-0 z-20 bg-white"
          data-testid="spec-paper-sticky-header"
        >
          <SectionBreadcrumb section={activeSection} />
          <ActionBar />
        </div>
        {visibleSections.length === 0 ? (
          <div className="flex h-full items-center justify-center text-sm text-zinc-500">
            Select a section in the left rail to start.
          </div>
        ) : (
          <div className="flex flex-col items-center gap-6 px-6 py-12">
            {visibleSections.map((s) => (
              <div
                key={s.id}
                ref={setSectionRef(s.id)}
                data-section-id={s.id}
                data-section-kind={s.section_kind}
                className="w-full max-w-[760px]"
              >
                {/* ClaimAnchorWarningBanner mounts BELOW the
                    section card so it's visually attached to the
                    section the gap belongs to. Previous placement
                    (above PaperSurface) put the banner between
                    sections — drafters reasonably misread it as
                    "the section ABOVE has a coverage gap" when
                    the gap actually belonged to the section BELOW.
                    Backend already restricts coverage checks to
                    detailed_desc_embodiments + summary
                    (spec.py:_CLAIM_COVERAGE_SECTIONS), so the
                    banner only mounts content for those two
                    section kinds — the visual association is the
                    bit drafters needed clarified. */}
                <PaperSurface section={s} embedded />
                <ClaimAnchorWarningBanner sectionId={s.id} />
                <UnregisteredNumeralBanner sectionId={s.id} />
                <RemovedFigureBanner sectionId={s.id} />
                <GenerationFindingsBanner sectionId={s.id} />
                <SectionDiffPanel sectionId={s.id} />
              </div>
            ))}
          </div>
        )}
      </div>
      <PromptBar />
      <FooterStatusStrip section={activeSection} />
    </div>
  );
}
